// cspell:ignore relayout
import type { AlignValue, RichTextSpan, TextBuffer, TextEngine, WrapValue } from '@meitu/x-design-text';
import { getUtf8ByteLength, measureDelegatedHostRuns } from '@/utils/text-kit';
import type { Optional } from '@/version-types';
import type { TextRenderInput, TextRenderSnapshot, TextRenderSpan, TextRenderSpanRange } from '../types';
import type { GlyphCache } from './glyph/cache';

/**
 * 不改变文字内容与字形选择、只需要重新 layout 的 Buffer 配置。
 *
 * `inlineSize` 已按书写方向选择 width/height 并扣除列表缩进；null 表示自适应行内尺寸。
 * `listMarkerIndent` 虽不直接传给 TextBuffer，但它变化时必须重新应用有效 inline size。
 * `wrap` 只决定断行策略、不改变字形选择，因此与其他 layout 参数一样可以增量更新。
 */
export type RelayoutConfig = {
    fontSize: number;
    lineHeightScale: number;
    letterSpacing: number;
    align: AlignValue;
    inlineSize: number | null;
    listMarkerIndent: number;
    wrap: WrapValue;
};

/**
 * 当前常驻 TextBuffer 已经成功应用的配置摘要。
 *
 * `richTextKey` 决定是否必须重新 shape，`relayout` 决定能否只调用局部 setter。只有完整排版和 snapshot
 * 读取成功后 Pipeline 才推进该状态，因此它始终描述 Buffer 中可以安全复用的已发布事实。
 */
export type AppliedBufferConfig = {
    richTextKey: string;
    relayout: RelayoutConfig;
};

/**
 * 单个 Renderer 私有的常驻 TextBuffer 及其已应用配置。
 */
export type BufferCacheEntry = {
    buffer: TextBuffer;
    applied: AppliedBufferConfig;
};

/**
 * 可以通过 TextBuffer setter 单独更新的排版字段。
 */
export type RelayoutField = keyof RelayoutConfig;

/**
 * 连续两次输入之间最小的 Buffer 更新计划。
 *
 * reuse 不操作 Buffer；relayout 只应用发生变化的 setter；reshape 重新设置书写模式、尺寸和完整 rich text。
 */
export type BufferUpdatePlan = { kind: 'reuse' } | { kind: 'relayout'; fields: RelayoutField[] } | { kind: 'reshape' };

/**
 * 一次输入预处理得到的 Engine spans 与可比较配置。
 */
export type PreparedBufferInput = {
    spans: RichTextSpan[];
    applied: AppliedBufferConfig;
};

/**
 * 把 Text Render span 转换为文字引擎 RichTextSpan。
 *
 * span 字号等于 Buffer 基础字号时省略局部 fontSize，避免等价输入因冗余 override 产生不同 shaping key。
 * 颜色仍传给 Engine 作为完整输入，但不进入 richTextKey；paint-only 更新由 snapshot color patch 处理。
 *
 * @param span - 宿主无关的文字分段。
 * @param baseFontSize - TextBuffer 使用的基础字号。
 * @returns 文字引擎可直接消费的富文本分段。
 */
const createEngineSpan = (span: TextRenderSpan, baseFontSize: number): RichTextSpan => ({
    text: span.text,
    attrs: {
        family: span.fontFamily,
        weight: span.bold ? 700 : 400,
        style: span.italic ? 'italic' : 'normal',
        underline: span.underline ? 'single' : undefined,
        underlinePosition: 'auto',
        decorationThickness: 'auto',
        strikethrough: span.lineThrough || undefined,
        colorRgba: span.colorRgba,
        ...(span.fontSize === baseFontSize ? {} : { fontSize: span.fontSize }),
    },
});

/**
 * 为没有显式 spans 的输入创建覆盖全文的默认 Engine span。
 *
 * @param input - 当前完整 Text Render 输入。
 * @returns 单个覆盖全文的 RichTextSpan。
 */
const createDefaultEngineSpan = (input: TextRenderInput): RichTextSpan => ({
    text: input.text,
    attrs: {
        family: input.fontFamily,
        weight: input.bold ? 700 : 400,
        style: input.italic ? 'italic' : 'normal',
        underline: input.underline ? 'single' : undefined,
        underlinePosition: 'auto',
        decorationThickness: 'auto',
        strikethrough: input.lineThrough || undefined,
        colorRgba: input.colorRgba,
    },
});

/**
 * 创建本轮实际交给 TextBuffer 的富文本分段。
 *
 * @param input - 当前完整 Text Render 输入。
 * @returns 显式 spans，或无 spans 时的单个默认分段。
 */
const createEngineSpans = (input: TextRenderInput): RichTextSpan[] =>
    input.spans.length > 0
        ? input.spans.map((span) => createEngineSpan(span, input.fontSize))
        : [createDefaultEngineSpan(input)];

/**
 * 计算非空 Engine spans 在完整 UTF-8 文本中的全局范围。
 *
 * 空 span 不产生范围，但仍推进 0 字节；结果顺序与 GlyphCache 过滤后的非空 styles 保持一致。
 *
 * @param spans - 本轮实际交给 TextBuffer 的富文本分段。
 * @returns 非空 spans 的全局 UTF-8 范围。
 */
const buildSpanRanges = (spans: readonly RichTextSpan[]): TextRenderSpanRange[] => {
    const ranges: TextRenderSpanRange[] = [];
    let offset = 0;
    spans.forEach((span) => {
        const length = getUtf8ByteLength(span.text);
        if (length > 0) ranges.push({ start: offset, end: offset + length });
        offset += length;
    });
    return ranges;
};

/**
 * 创建决定是否需要重新 shape 的稳定输入键。
 *
 * 键包含文本、resolved font、粗斜体、装饰线、书写模式和真正生效的局部字号覆盖；颜色以及只影响
 * layout 的基础字号、行高、字距、对齐和行内尺寸不进入此键。这样 paint-only 可以补丁 snapshot，
 * relayout-only 可以复用同一个 shaped Buffer。
 *
 * @param input - 已完成字体解析的 Text Render 输入。
 * @returns 可比较连续输入 shaping 身份的字符串。
 */
const createRichTextKey = (input: TextRenderInput): string => {
    const spans = input.spans.length > 0 ? input.spans : [input];
    return JSON.stringify({
        writingMode: input.writingMode,
        spans: spans.map((span) => ({
            text: span.text,
            fontFamily: span.fontFamily,
            bold: span.bold,
            italic: span.italic,
            underline: span.underline,
            lineThrough: span.lineThrough,
            fontSizeOverride: span.fontSize === input.fontSize ? null : span.fontSize,
        })),
    });
};

/**
 * 将 Text Render 输入归一化为 Engine spans 与 Buffer 更新摘要。
 *
 * 横排只消费 width，竖排只消费 height；固定尺寸扣除列表 marker 占用并至少保留 1，null 继续表达
 * 自适应尺寸。该归一化保证无关交叉轴变化不会触发多余 relayout。
 *
 * `noWrap` 映射为引擎的 `none` 断行策略；缺省时使用引擎默认的 `wordOrGlyph`，与未声明该字段前的
 * 行为完全一致。
 *
 * @param input - 已完成字体解析的 Text Render 输入。
 * @param listMarkerIndent - 同轮列表 marker shaping 得到的正文缩进。
 * @returns 可直接应用并与上次成功状态比较的预处理结果。
 */
export const prepareBufferInput = (input: TextRenderInput, listMarkerIndent: number): PreparedBufferInput => {
    const declaredInlineSize = input.writingMode === 'horizontal-tb' ? input.width : input.height;
    return {
        spans: createEngineSpans(input),
        applied: {
            richTextKey: createRichTextKey(input),
            relayout: {
                fontSize: input.fontSize,
                lineHeightScale: input.lineHeightScale,
                letterSpacing: input.letterSpacing,
                align: input.align,
                inlineSize: declaredInlineSize === null ? null : Math.max(declaredInlineSize - listMarkerIndent, 1),
                listMarkerIndent,
                wrap: input.noWrap ? 'none' : 'wordOrGlyph',
            },
        },
    };
};

/**
 * 比较上次成功配置与当前配置，选择最小 Buffer 更新方式。
 *
 * 首次使用或 richTextKey 变化必须 reshape；仅 relayout 字段变化时返回精确字段列表；完全相同时 reuse。
 *
 * @param previous - 常驻 Buffer 上次完整成功应用的配置。
 * @param next - 当前输入预处理后的目标配置。
 * @returns 本轮最小更新计划。
 */
export const classifyBufferUpdate = (
    previous: Optional<AppliedBufferConfig>,
    next: AppliedBufferConfig,
): BufferUpdatePlan => {
    if (!previous || previous.richTextKey !== next.richTextKey) return { kind: 'reshape' };
    const fields = (Object.keys(next.relayout) as RelayoutField[]).filter(
        (field) => previous.relayout[field] !== next.relayout[field],
    );
    return fields.length > 0 ? { kind: 'relayout', fields } : { kind: 'reuse' };
};

/**
 * 使用当前基础排版参数创建 Renderer 私有 TextBuffer。
 *
 * 书写模式、尺寸与 rich text 由后续 `applyBufferUpdate()` 统一设置，使新 Buffer 与复用 Buffer 走同一
 * reshape 协议。
 *
 * @param engine - 当前 ready 的文字引擎。
 * @param input - 当前完整 Text Render 输入。
 * @returns 新创建、尚未应用正文的 TextBuffer。
 */
export const createSnapshotBuffer = (engine: TextEngine, input: TextRenderInput): TextBuffer =>
    engine.createBuffer({
        fontSize: input.fontSize,
        lineHeightScale: input.lineHeightScale,
        letterSpacing: input.letterSpacing,
        align: input.align,
    });

/**
 * 将返回 Result 的 TextBuffer setter 失败统一转换为异常。
 *
 * Pipeline 捕获异常后会释放已被部分修改的 Buffer，避免未知内部状态进入下一轮复用。
 *
 * @param result - Engine setter 返回结果。
 * @param name - 用于错误信息的 setter 名称。
 * @returns 无返回值；失败时抛出异常。
 */
const expectSetter = (result: { ok: boolean; error?: unknown }, name: string): void => {
    if (!result.ok) throw new Error(`TextBuffer.${name} failed: ${String(result.error ?? 'unknown error')}`);
};

/**
 * 按更新计划把当前输入应用到常驻 TextBuffer。
 *
 * reshape 完整设置书写模式、断行策略、有效尺寸和 rich text，并只调用一次 `setRichText()`；复用旧
 * Buffer 时还要重置可能变化的基础参数。relayout 只调用变化字段对应的 setter，reuse 完全不触碰 Buffer。
 * 方法只负责写入，不执行 layout，也不推进 Pipeline 的 applied 状态。
 *
 * @param buffer - 当前 Renderer 私有 TextBuffer。
 * @param input - 当前完整 Text Render 输入。
 * @param prepared - 本轮预处理后的 spans 与配置。
 * @param plan - 与上次成功状态比较得到的最小更新计划。
 * @param isNewBuffer - 是否为刚创建、基础参数已经由 createBuffer 应用的 Buffer。
 * @returns 无返回值；返回 Result 的 setter 失败时抛出异常。
 */
export const applyBufferUpdate = (
    buffer: TextBuffer,
    input: TextRenderInput,
    prepared: PreparedBufferInput,
    plan: BufferUpdatePlan,
    isNewBuffer = false,
): void => {
    const { relayout } = prepared.applied;
    if (plan.kind === 'reshape') {
        buffer.setWritingMode(input.writingMode);
        // 断行策略不随 createBuffer 传入，新旧 Buffer 都在这里对齐；引擎对同值调用是空操作。
        buffer.setWrap(relayout.wrap);
        if (!isNewBuffer) {
            expectSetter(buffer.setFontSize(relayout.fontSize), 'setFontSize');
            expectSetter(buffer.setLineHeightScale(relayout.lineHeightScale), 'setLineHeightScale');
            buffer.setLetterSpacing(relayout.letterSpacing);
            buffer.setAlign(relayout.align);
        }
        buffer.setSize(
            input.writingMode === 'horizontal-tb' ? relayout.inlineSize : null,
            input.writingMode === 'vertical-rl' ? relayout.inlineSize : null,
        );
        buffer.setRichText(prepared.spans);
        return;
    }
    if (plan.kind === 'reuse') return;

    if (plan.fields.includes('fontSize')) expectSetter(buffer.setFontSize(relayout.fontSize), 'setFontSize');
    if (plan.fields.includes('lineHeightScale')) {
        expectSetter(buffer.setLineHeightScale(relayout.lineHeightScale), 'setLineHeightScale');
    }
    if (plan.fields.includes('letterSpacing')) buffer.setLetterSpacing(relayout.letterSpacing);
    if (plan.fields.includes('align')) buffer.setAlign(relayout.align);
    if (plan.fields.includes('wrap')) buffer.setWrap(relayout.wrap);
    if (
        relayout.inlineSize === null ||
        plan.fields.includes('inlineSize') ||
        plan.fields.includes('listMarkerIndent')
    ) {
        buffer.setSize(
            input.writingMode === 'horizontal-tb' ? relayout.inlineSize : null,
            input.writingMode === 'vertical-rl' ? relayout.inlineSize : null,
        );
    }
};

/**
 * 执行 TextBuffer layout，并完成浏览器 delegated run 的宿主测量闭环。
 *
 * 固定行内尺寸只需一次 layout/measurement。自适应横排或竖排先获取自然行内尺寸，再把该尺寸写回
 * Buffer 并执行第二次 layout/measurement，保持最终行拆分、宿主测量和发布尺寸属于同一稳定状态。
 * 任一宿主测量失败都会抛出，由 Pipeline 释放已修改 Buffer。
 *
 * @param buffer - 已应用当前输入的 TextBuffer。
 * @param input - 当前完整 Text Render 输入，用于判断书写方向。
 * @param inlineSize - 当前有效固定行内尺寸；null 表示自适应。
 * @returns 无返回值；宿主测量失败时抛出异常。
 */
export const layoutSnapshotBuffer = (buffer: TextBuffer, input: TextRenderInput, inlineSize: number | null): void => {
    buffer.layout();
    const measurement = measureDelegatedHostRuns(buffer);
    if (!measurement.ok) throw measurement.error;
    if (inlineSize !== null) return;

    const isHorizontal = input.writingMode === 'horizontal-tb';
    const naturalInlineSize = Math.max(
        Math.ceil(isHorizontal ? buffer.getContentWidth() : buffer.getContentHeight()),
        1,
    );
    buffer.setSize(isHorizontal ? naturalInlineSize : null, isHorizontal ? null : naturalInlineSize);
    buffer.layout();
    const finalMeasurement = measureDelegatedHostRuns(buffer);
    if (!finalMeasurement.ok) throw finalMeasurement.error;
};

/**
 * 从已完成 layout 的常驻 TextBuffer 读取不可变 snapshot。
 *
 * 先读取尺寸、line metrics、GlyphInfo 与 span ranges，再尝试通过 GlyphCache 重建完整 instructions；
 * 任一模板未命中或不可缓存时，整轮调用 `getRenderInstructions()` 并在完整映射校验后学习模板。
 * 两条路径不会混合发布。颜色补丁由 Pipeline 在返回后统一应用，防止常驻 Buffer 中的旧颜色回流。
 *
 * @param buffer - 已完成当前输入排版与宿主测量的 TextBuffer。
 * @param input - 当前完整 Text Render 输入。
 * @param spans - 本轮实际交给 TextBuffer 的 Engine spans。
 * @param listMarkerIndent - 同轮列表 marker shaping 得到的正文缩进。
 * @param glyphCache - 编辑器实例内共享的字形模板缓存。
 * @returns 当前输入对应的只读 snapshot；Engine 没有可用输出时返回 null。
 */
export const readSnapshot = (
    buffer: TextBuffer,
    input: TextRenderInput,
    spans: readonly RichTextSpan[],
    listMarkerIndent: number,
    glyphCache: GlyphCache,
): TextRenderSnapshot | null => {
    const contentWidth = Math.max(Math.ceil(buffer.getContentWidth()), 0);
    const contentHeight = Math.max(Math.ceil(buffer.getContentHeight()), 0);
    const isHorizontal = input.writingMode === 'horizontal-tb';
    const declaredInlineSize = isHorizontal ? input.width : input.height;
    const inlineSize =
        declaredInlineSize === null
            ? Math.max(isHorizontal ? contentWidth : contentHeight, 1)
            : Math.max(declaredInlineSize - listMarkerIndent, 1);
    const emptyInlineStart = input.align === 'center' ? inlineSize / 2 : input.align === 'right' ? inlineSize : 0;
    // 引擎对没有字符的行/列返回行内坐标 0，即使内容框指定了居中或靠右对齐。
    // 矢量空文本进入编辑态时使用居中兜底光标，回车后改用这些真实行数据，光标就会跳到左边缘；
    // 有文字后的尾部空行、段落间空行也存在同样问题，竖排则表现为跳到顶部。
    // 在共享快照边界补齐空行的对齐坐标，让光标、选区与导航消费一致的几何，保留块轴及字节范围。
    // 可用尺寸与 Buffer 设置一致：固定尺寸扣除列表缩进，自适应使用二次排版的自然尺寸；
    // 列表缩进仍由几何消费者统一叠加，不能在这里重复计入。
    const lineMetrics = Object.freeze(
        buffer.getLineMetrics().map((line) => {
            if (line.byteStart !== line.byteEnd || emptyInlineStart === 0) return line;
            return isHorizontal
                ? { ...line, leftEdgeX: emptyInlineStart, rightEdgeX: emptyInlineStart }
                : { ...line, top: emptyInlineStart, bottom: emptyInlineStart };
        }),
    );
    const glyphInfos = Object.freeze([...buffer.getGlyphInfos()]);
    const spanRanges = Object.freeze(buildSpanRanges(spans));
    let instructions = glyphCache.rebuild(input, spanRanges, glyphInfos);
    if (!instructions) {
        instructions = [...buffer.getRenderInstructions()];
        glyphCache.learn(input, spanRanges, glyphInfos, instructions);
    }

    // 默认文字新增后会自动全选，直接回车会把正文替换成纯换行；引擎仍会生成有效的多行几何，
    // 但横排行内宽度（竖排行内高度）为 0，且没有字形绘制指令。旧判断要求宽高和指令都非空，
    // 导致这类合法排版返回 null，Pipeline 随即抛错并让 Renderer 发布 failed。
    // 编辑会话因此回退到旧 snapshot，SVG Preview 也保留旧文字：新光标偏移被映射到旧行上，
    // 表现为回车后光标回到开头、文字仍在，继续回车也无法换行。
    // 排版有效性只要求存在行和正的块轴尺寸；行内尺寸为 0 或没有字形不代表排版失败。
    // 保留这些空行才能推进光标，并发布空白 SVG 替换旧画面，不能把纯换行当作空字符串清除几何。
    const blockSize = input.writingMode === 'horizontal-tb' ? contentHeight : contentWidth;
    if (blockSize <= 0 || lineMetrics.length === 0) return null;
    return Object.freeze({
        writingMode: input.writingMode,
        instructions: Object.freeze(instructions),
        glyphInfos,
        lineMetrics,
        spanRanges,
        listMarkerIndent,
        contentWidth,
        contentHeight,
        minInlineSize: Math.max(
            Math.ceil(
                input.writingMode === 'horizontal-tb' ? buffer.getContentMinWidth() : buffer.getContentMinHeight(),
            ),
            0,
        ),
    });
};
